import { FormikProvider, useFormik } from 'formik';
import { HuddleCashBudget } from 'lib/generated/graphql';
import Image from 'next/image';
import { forwardRef, useImperativeHandle } from 'react';
import options from 'src/screens/company/components/CreateProject/FormBudget/FormBudgetOptions';
import { Select, TextArea } from 'src/shared-components/Form';
import * as Yup from 'yup';

const budgetOptions = {
  preferredStartDates: [
    {
      value: 'ASAP',
      label: 'ASAP',
    },
    {
      value: 'NEXT_MONTH',
      label: 'In the next month',
    },
    {
      value: 'NEXT_COUPLE_OF_MONTHS',
      label: 'In the next three months',
    },
  ],
};

const placeHolderArr = ['2.png', '3.png', '5.png'].map(name => `/images/memoji/${name}`);

interface ProjectFormProps {
  onFormSubmitted: (values: any) => void;
  initialValues: any;
  update: boolean;
}

const ProjectForm = forwardRef(({ onFormSubmitted, initialValues, update = false }: ProjectFormProps, ref) => {
  const form = useFormik({
    initialValues,
    validationSchema: Yup.object({
      preferredStartDate: Yup.string().required('Hey we need this to get going'),
      preferredDuration: Yup.number(),
      cashBudget: Yup.string().required('Please select an option, we need this to get going'),
      equityOrCrypto: Yup.bool().required("We need to know if you're willing to pay with equity or crypto"),
      brief: Yup.string().required('Hey we need a project brief to get going'),
    }),
    onSubmit: values => {
      /* istanbul ignore next */
      if (update) {
        onFormSubmitted({
          preferredStartDate: { set: values.preferredStartDate },
          preferredDuration: { set: values.preferredDuration },
          cashBudget: { set: values.cashBudget },
          equityOrCrypto: { set: values.equityOrCrypto },
          brief: { set: values.brief },
        });
      } else {
        onFormSubmitted({
          preferredStartDate: values.preferredStartDate,
          preferredDuration: values.preferredDuration,
          cashBudget: values.cashBudget,
          equityOrCrypto: values.equityOrCrypto,
          brief: values.brief,
        });
      }
    },
  });

  useImperativeHandle(ref, () => ({
    submitForm() {
      form.submitForm();
    },
  }));

  const briefPlaceholder = `Great work starts with a clear scope. Whether it's 1-2-3 Huddlers or more, we need to know the ins and outs of what you're creating to make a great match. Focus your brief on what you're trying to accomplish (instead of who you need.)\n\nExample briefs can start with...\n• We're looking to rebrand our company\n• I need ongoing product (UX/UI) support including big-picture thinking and execution\n• I'm building a web application / AI tool / enterprise SaaS and I need engineering help`;

  return (
    <FormikProvider value={form}>
      <div data-testid="form-budget" className="grid grid-cols-6 gap-4 text-left">
        <div className="col-span-6 sm:col-span-6">
          <TextArea
            id="brief"
            name="brief"
            autoComplete="off"
            label="Brief"
            placeholder={briefPlaceholder}
            required
            height="h-40"
          />
        </div>
        <div className="col-span-6 sm:col-span-6 md:col-span-2">
          <Select
            data-testid="cashBudget"
            options={[
              {
                value: HuddleCashBudget.Range_5000_10000,
                label: '$5,000 - $10,000',
              },
              {
                value: HuddleCashBudget.Range_10000_25000,
                label: '$10,000 - $25,000',
              },
              {
                value: HuddleCashBudget.Range_25000_50000,
                label: '$25,000 - $50,000',
              },
              {
                value: HuddleCashBudget.Range_50000_100000,
                label: '$50,000 - $100,000',
              },
              {
                value: HuddleCashBudget.Range_100000_150000,
                label: '$100,000 - $150,000',
              },
              {
                value: HuddleCashBudget.Range_150000_200000,
                label: '$150,000 - $200,000',
              },
              {
                value: HuddleCashBudget.Range_200000_250000,
                label: '$200,000 - $250,000',
              },
              {
                value: HuddleCashBudget.Range_250000Plus,
                label: '$250,000 +',
              },
            ]}
            label="Cash budget"
            name="cashBudget"
            required
            placeholder="Select one..."
          />
        </div>
        <div className="col-span-6 sm:col-span-6 md:col-span-2">
          <Select
            data-testid="preferredStartDate"
            options={budgetOptions.preferredStartDates}
            label="When you want to start"
            name="preferredStartDate"
            required
            placeholder="Select one..."
          />
        </div>
        <div className="col-span-6 sm:col-span-6 md:col-span-2">
          <Select
            data-testid="preferredDuration"
            options={options.durations}
            label="Duration"
            name="preferredDuration"
            required
            placeholder="Select one..."
            onChange={e => {
              form.setFieldValue('preferredDuration', parseInt(e.target.value));
            }}
          />
        </div>
        <div className="flex items-center mt-1 col-span-6 sm:col-span-6 p-3 bg-[#EFF9F1] rounded-xl">
          <input
            className="w-5 h-5 text-[#323232] bg-[#323232] rounded focus:ring-[#323232] focus:ring-2"
            type="checkbox"
            checked={form.values.equityOrCrypto}
            onChange={() => {
              form.setFieldValue('equityOrCrypto', !form.values.equityOrCrypto);
            }}
          />
          <label className="text-sm font-medium text-gray-900 ml-3">Open to paying builders partly in equity</label>
          <div className="flex ml-auto">
            <div className="h-8 w-8 z-30 bg-white rounded-full flex items-center justify-center">💸</div>
            {placeHolderArr.map((img, index) => (
              <div
                key={img}
                className={`h-8 w-8 -ml-1 relative rounded-xl outline outline-2 outline-[#EFF9F1]`}
                style={{ zIndex: placeHolderArr.length - index }}
              >
                <Image className="rounded-xl" layout="fill" src={img} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </FormikProvider>
  );
});

export default ProjectForm;
